import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXCLUDED = ["/admin", "/login"];

function getVisitorId() {
  let id = localStorage.getItem("sf-visitor-id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("sf-visitor-id", id);
  }
  return id;
}

function getDevice() {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return "tablet";
  if (/Mobi|iPhone|Android.*Mobile/i.test(ua)) return "mobile";
  return "desktop";
}

function getExternalReferrer() {
  try {
    if (document.referrer) {
      const host = new URL(document.referrer).hostname;
      if (host && host !== window.location.hostname) return host.replace(/^www\./, "");
    }
  } catch {
    /* noop */
  }
  return null;
}

// UTM first-touch: catturati dal primo ingresso della sessione e allegati a ogni pageview
function getUtm() {
  try {
    const q = new URLSearchParams(window.location.search);
    const found = {};
    ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
      const v = q.get(k);
      if (v) found[k] = v.slice(0, 100);
    });
    if (Object.keys(found).length) {
      sessionStorage.setItem("sf-utm", JSON.stringify(found));
      return found;
    }
    return JSON.parse(sessionStorage.getItem("sf-utm") || "{}");
  } catch {
    return {};
  }
}

export default function AnalyticsTracker() {
  const { pathname } = useLocation();
  const state = useRef({ path: null, start: 0, maxScroll: 0 });

  const sendEngagement = useCallback(() => {
    const s = state.current;
    if (!s.path || EXCLUDED.some((p) => s.path.startsWith(p))) return;
    const duration = Date.now() - s.start;
    if (duration < 1000) return;
    const payload = JSON.stringify({
      path: s.path,
      visitor_id: getVisitorId(),
      duration_ms: Math.round(duration),
      scroll_pct: Math.round(s.maxScroll),
    });
    navigator.sendBeacon(`${API}/track/engagement`, new Blob([payload], { type: "application/json" }));
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const pct = max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 100;
      if (pct > state.current.maxScroll) state.current.maxScroll = pct;
    };
    const onHide = () => sendEngagement();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onHide);
    };
  }, [sendEngagement]);

  useEffect(() => {
    sendEngagement();
    if (EXCLUDED.some((p) => pathname.startsWith(p))) {
      state.current = { path: null, start: 0, maxScroll: 0 };
      return;
    }
    state.current = { path: pathname, start: Date.now(), maxScroll: 0 };
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: pathname,
        visitor_id: getVisitorId(),
        referrer: getExternalReferrer(),
        device: getDevice(),
        ...getUtm(),
      }),
    }).catch(() => {});
  }, [pathname, sendEngagement]);

  return null;
}
